import * as Sentry from "@sentry/browser";
import { Col, Row, Table, Tag, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import queryString from "query-string";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import {
  Audit,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  injectKey,
  request,
} from "../helpers/utilities";
import { ALL_AUDITABLE_TYPES_QUERY, GET_AUDITS } from "../gql/general";
import { ALL_USERS_QUERY } from "../gql/users";
import moment from "moment";
import { DEBOUNCE_TIME, PAGE_SIZE } from "../helpers/constants";
import { PageToolbar } from "../components/PageToolbar";
import { IField } from "../components/AdvancedSearchModal";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface PaginationQueryParams {
  page: number;
  userId?: string;
  auditableType?: string;
  action?: string;
  period?: string;
}

interface ModelsInterface {
  [key: string]: string;
}

const MapSingularModelToPlural: ModelsInterface = {
  DEPARTMENT: "departments",
  AUDIT: "audits",
  USER: "users",
  ROLE: "roles",
  CUSTOMERSUBSCRIPTION: "customers",
};

interface State {
  isFiltering: boolean;
  auditLogs: Audit[];
  filteredAudits: Audit[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  page: number;
  count: number;
  action: string;
  auditableType: string;
  userId: string;
  users: User[];
  auditableTypes: string[];
  period: string;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export const columns = [
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      if (!record.user) {
        return <span>System User</span>;
      } else {
        return <span>{record.user.name}</span>;
      }
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "actions",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      return <span>{capitalizeFirstLetter(record.action)}</span>;
    },
  },
  {
    title: "Model",
    dataIndex: "model",
    key: "model",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      return (
        <span>
          <Tag>{record.auditableType}</Tag>
        </span>
      );
    },
  },
  {
    title: "Resource Id",
    dataIndex: "resourceId",
    align: "center" as const,
    key: "resourceId",
    render(text: string, record: Audit): JSX.Element {
      const model = record.auditableType && record.auditableType.toUpperCase();
      const mapping = model && MapSingularModelToPlural[model];
      const resourceId =
        model === "CUSTOMERSUBSCRIPTION"
          ? record.customerId
          : record.auditableId;
      if (mapping && resourceId) {
        return (
          <NavLink to={`${mapping}/${resourceId}`}>
            {record.auditableId}
          </NavLink>
        );
      } else {
        return <span>{record.auditableId}</span>;
      }
    },
  },
  {
    title: "Date",
    dataIndex: "datetime",
    key: "datetime",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      const formattedDateTime = moment(record.createdAt).format("llll");
      return <span>{formattedDateTime}</span>;
    }
  },
];

export function remodelAuditableTypes(
  auditableTypes: string[],
): SelectOptionsInterface[] {
  const remodeled: SelectOptionsInterface[] = [];
  for (const type of auditableTypes) {
    remodeled.push({
      id: type,
      name: type,
    });
  }
  return remodeled;
}

export function buildVariables(
  values: PaginationQueryParams,
): Record<string, unknown> {
  let builtVariables: Record<string, unknown> = {
    page: values.page,
    period: values.period,
  };
  if (values.action) {
    builtVariables = { ...builtVariables, action: values.action };
  }
  if (values.userId) {
    builtVariables = { ...builtVariables, userId: values.userId };
  }
  if (values.auditableType) {
    builtVariables = { ...builtVariables, auditableType: values.auditableType };
  }

  return builtVariables;
}

export default class Audits extends React.Component<
  RouteComponentProps,
  State
> {
  filterReference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      auditLogs: [],
      filteredAudits: [],
      isFiltering: false,
      page: 1,
      count: 1,
      action: "",
      auditableType: "",
      userId: "",
      users: [],
      auditableTypes: [],
      period: "0",
      selectedFieldsToDisplay: [],
      removedField: undefined,
    };
    this.filterReference = React.createRef<FormInstance>();
  }
  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.filterReference = ref;
  };

  extractQueryParamsFromUrl = (): PaginationQueryParams => {
    const parsed = queryString.parse(window.location.search);

    return JSON.parse(JSON.stringify(parsed)) as PaginationQueryParams;
  };

  mergePageNumberInQueryParamsWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();

    this.setState({
      page: parsed.page || 1,
      userId: parsed.userId || "",
      action: parsed.action || "",
      auditableType: parsed.auditableType || "",
      period: parsed.period || "0",
    });
  };

  syncBrowserQueryString = (): void => {
    const { page, action, auditableType, userId, period } = this.state;
    this.props.history.replace(
      `?page=${page}&user=${userId}&model=${auditableType}&action=${action}&period=${period}`,
    );
  };

  handleFilter = (
    key: string,
    value: string | string[],
  ): void => {
    this.setState(
      {
        [key]: value,
        page: 1,
        isLoading: true,
      } as any,
      () => {
        this.getAudits(1);
      },
    );
  };

  getAudits = async (pageNo: number): Promise<void> => {
    interface Response {
      data: {
        audits: {
          count: number;
          values: Audit[];
        };
      };
    }
    const page = pageNo;
    const { action, auditableType, userId, period } = this.state;
    const vars: PaginationQueryParams = {
      page,
      auditableType,
      userId,
      action,
      period,
    };

    const returnedVariables = buildVariables(vars);
    try {
      const r: AxiosResponse<Response> = await request({
        query: GET_AUDITS,
        variables: returnedVariables,
      });

      this.setState({
        count: r.data.data.audits.count,
        auditLogs: r.data.data.audits.values,
        page: pageNo,
      });
    } catch (error) {
      message.error(`Failed to load audits - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      await this.syncBrowserQueryString();
    }
  };

  getAllUsers = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: ALL_USERS_QUERY,
        variables: {
          page: 1,
          q: this.state.filterBy,
        },
      });
      this.setState({
        users: data.users.results,
      });
    } catch (error) {
      // Fail silently for background loads or handle as needed
    } finally {
      this.setState({ isLoading: false });
    }
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  getAllAudtiableTypes = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: ALL_AUDITABLE_TYPES_QUERY,
      });
      this.setState({
        auditableTypes: data.auditableModels,
      });
    } catch (error) {
       // Fail silently
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ filterBy: searchTerm });
    await this.triggerDelayedSearch();
  };

  async componentDidMount(): Promise<void> {
    await this.mergePageNumberInQueryParamsWithLocalState();
    this.getAudits(this.state.page);
    this.getAllUsers();
    this.getAllAudtiableTypes();
  }

  render(): JSX.Element | null {
    const { auditLogs, page, count, action, auditableType, userId, period, users, auditableTypes } = this.state;
    const hasFilters = !!(userId || auditableType || action || period !== "0");

    return (
        <Card>
          <div className="auditLogs-table-container">
            <PageToolbar modelName="audits"
              totalCount={count}
              currentDataCount={auditLogs.length}
              page={page}
              pageSize={PAGE_SIZE}
              filters={
                <PageToolbar.Filters
                  isFiltering={hasFilters}
                  onClear={() => {
                    this.setState({
                      userId: "",
                      auditableType: "",
                      action: "",
                      period: "0",
                      page: 1,
                      isLoading: true
                    }, () => this.getAudits(1));
                  }}
                  configs={[
                    {
                      label: "User",
                      options: users.map(user => ({ value: user.id, label: user.name })),
                      selectedValues: userId ? [userId] : [],
                      onChange: (values) => this.handleFilter("userId", values[0] || ""),
                      onSearch: this.handleSearchInput,
                      placeholder: "Filter by User",
                    },
                    {
                      label: "Model",
                      options: auditableTypes.map(type => ({ value: type, label: type })),
                      selectedValues: auditableType ? [auditableType] : [],
                      onChange: (values) => this.handleFilter("auditableType", values[0] || ""),
                      placeholder: "Filter by Model",
                    },
                    {
                      label: "Action",
                      options: [
                        { value: "create", label: "Create" },
                        { value: "read", label: "Read" },
                        { value: "update", label: "Update" },
                        { value: "delete", label: "Delete" },
                      ],
                      selectedValues: action ? [action] : [],
                      onChange: (values) => this.handleFilter("action", values[0] || ""),
                      placeholder: "Filter by Action",
                    },
                    {
                      label: "Period",
                      options: [
                        { value: "30", label: "30 days ago" },
                        { value: "60", label: "60 days ago" },
                        { value: "90", label: "90 days ago" },
                      ],
                      selectedValues: period && period !== "0" ? [period] : [],
                      onChange: (values) => this.handleFilter("period", values[0] || "0"),
                      placeholder: "Filter by Period",
                    }
                  ]}
                />
              }
            />
            <Row>
              <Col span={24}>
                <Table
                  loading={this.state.isLoading}
                  scroll={{ x: 500 }}
                  columns={columns}
                  dataSource={injectKey(auditLogs, "id")}
                  onRow={(record) => ({
                    onClick: () => {
                      this.props.history.push({
                        pathname: `/audits/${record.id}`,
                      });
                    },
                    style: { cursor: 'pointer' }

                  })}
                  pagination={{
                    total: count,
                    current: page,
                    defaultPageSize: 25,
                    onChange: (page) => {
                      this.setState({ isLoading: true, page }, () => this.getAudits(page));
                    },
                  }}
                />
              </Col>
            </Row>
          </div>
        </Card>
    );
  }
}
